import { Autocomplete, Box, ListItemText } from '@freighthub/silhouette-core'
import { find } from 'lodash-es'
import { useObserver } from 'mobx-react-lite'
import { FC, ReactNode, useState } from 'react'
import { useI18n } from 'utils/localisation'

import { Freightie, IFreightieJSON, TActor, TUser } from '../../Actors'
import { INITIALS_AVATAR, THUMBNAIL_AVATAR } from '../../avatars/AvatarType'
import { getUserType } from '../../avatars/avatarUtils'
import { SubscriptionAvatar } from '../../avatars/SubscriptionAvatar'
import { FREIGHTIE_ID, TIdType, TSubscription } from '../SubscriptionModel'

type MembersAutoCompleteSelection = {
  label: string
  value: string
  type: TIdType
  actor: TActor
}

export type MembersAutoCompleteProps = {
  subscription: TSubscription
  users: TUser[]
  addSubscriber: (id: string, idType: TIdType) => Promise<void>
  getFreighties?: (name: string) => Promise<IFreightieJSON[]>
}

type AutoCompleteItemProps = Pick<MembersAutoCompleteSelection, 'actor'>

export const AutoCompleteItem: FC<AutoCompleteItemProps> = ({ actor }) => {
  const renderAvatar = (): ReactNode => {
    const isValidThumbnailUrl = 'thumbnailUrl' in actor && actor.thumbnailUrl
    const avatar = isValidThumbnailUrl ? actor.thumbnailUrl : actor.initials
    const avatarType = isValidThumbnailUrl ? THUMBNAIL_AVATAR : INITIALS_AVATAR
    return (
      <SubscriptionAvatar
        data-testid={avatarType}
        avatar={avatar}
        avatarType={avatarType}
        userType={getUserType(actor._id)}
        disabled
        displayName={false}
      />
    )
  }

  return (
    <>
      <Box mr={1}>{renderAvatar()}</Box>
      <ListItemText primary={actor.name} secondary={actor.email} />
    </>
  )
}

export const MembersAutoComplete: FC<MembersAutoCompleteProps> = ({
  users,
  subscription,
  addSubscriber,
  getFreighties,
}) => {
  const [freighties, setFreighties] = useState<IFreightieJSON[]>([])
  const [value, setValue] = useState<MembersAutoCompleteSelection | null>()
  const { translate } = useI18n()

  const { freightieIds } = subscription

  const getOptions = (): MembersAutoCompleteSelection[] => {
    const userOptions = users.map(
      (user): MembersAutoCompleteSelection => ({
        label: user.name,
        value: user._id,
        actor: user,
        type: 'userId',
      })
    )
    const freightieOptions = freighties
      .filter((freightie) => !find(freightieIds, (freightieId) => freightie._id === freightieId))
      .map(
        (freightie): MembersAutoCompleteSelection => ({
          label: freightie.name!,
          value: freightie._id,
          actor: freightie as TActor,
          type: 'freightieId',
        })
      )
    return [...userOptions, ...freightieOptions]
  }

  const handleSubscriberSelection = (_, selection: MembersAutoCompleteSelection): void => {
    if (selection) {
      const {
        actor: { _id },
        actor,
        type,
      } = selection
      setValue(null)
      if (type === FREIGHTIE_ID) {
        subscription.activityFeed.actors.addFreightie(actor)
      }
      addSubscriber(_id, type)
    }
  }

  const onInputChange = async (_, searchText: string) => {
    if (getFreighties && searchText.length > 1) {
      try {
        const savedFreighties = await getFreighties(searchText)
        setFreighties(
          savedFreighties
            .filter((freightie) => !find(freightieIds, (freightieId) => freightie._id === freightieId))
            .map((freightie) => Freightie.create(freightie))
        )
      } catch {
        setFreighties([])
      }
    }
  }

  return useObserver(() => (
    <Autocomplete
      onInputChange={onInputChange}
      value={value}
      options={getOptions()}
      className="basic-multi-select"
      renderOption={AutoCompleteItem}
      onChange={handleSubscriberSelection}
      noOptionsText={translate('members-auto-complete-no_options')}
      getOptionLabel={(option: MembersAutoCompleteSelection) => option.label}
    />
  ))
}
